import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useStickyDetection } from '@/hooks/useStickyDetection';
import SvgCaret from '@common/Icons/Caret';
import SvgHouse from '@common/Icons/House';
import { useLayoutContext } from '@common/Layout/LayoutProvider/LayoutContext';

import {
  NavigationItem,
  PageNavigationBar,
} from '../../../common/Providers/GraphqlProvider/generated';
import { compareLinkUrlAndRoute } from '../../../common/Utils/utils';
import LinkNavigation from './LinkNavigation';
import NavigationBarSkeleton from './NavigationBarSkeleton';

const NavigationBar = ({
  currentPageNavigationBar,
  currentNavigationItems,
  isLoading,
}: {
  currentPageNavigationBar?: PageNavigationBar;
  currentNavigationItems?: NavigationItem[];
  isLoading?: boolean;
}) => {
  const { route, asPath } = useRouter();
  const isSticky = useStickyDetection(10);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollRight, setShowScrollRight] = useState(false);
  const [showScrollLeft, setShowScrollLeft] = useState(false);

  const { layout } = useLayoutContext();
  const { navigationBar: navigationBarContext, navigationItems: navigationItemsContext } =
    layout || {};

  // Use new navigationItems if available, fallback to old pattern for backward compatibility
  const sortedDataNavigationItem = useMemo(() => {
    const items =
      currentNavigationItems ||
      navigationItemsContext ||
      currentPageNavigationBar?.rootItem?.children ||
      navigationBarContext?.rootItem?.children ||
      [];
    // Sort by orderIndex
    return [...items].sort((a, b) => a.orderIndex - b.orderIndex);
  }, [
    currentNavigationItems,
    navigationItemsContext,
    currentPageNavigationBar,
    navigationBarContext,
  ]);

  // Detectar si hay overflow
  const checkOverflow = () => {
    const container = containerRef.current;
    if (container) {
      const hasOverflowRight =
        container.scrollWidth > container.clientWidth + container.scrollLeft + 1;
      const hasOverflowLeft = container.scrollLeft > 0;
      setShowScrollRight(hasOverflowRight);
      setShowScrollLeft(hasOverflowLeft);
    }
  };

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [sortedDataNavigationItem]);

  const scrollRight = () => {
    const container = containerRef.current;
    if (container) {
      container.scrollBy({ left: 200, behavior: 'smooth' });
      setTimeout(checkOverflow, 300);
    }
  };

  const scrollLeft = () => {
    const container = containerRef.current;
    if (container) {
      container.scrollBy({ left: -200, behavior: 'smooth' });
      setTimeout(checkOverflow, 300);
    }
  };

  // Mostrar skeleton mientras está cargando y no hay items
  if (isLoading && sortedDataNavigationItem.length === 0) {
    return <NavigationBarSkeleton />;
  }

  return (
    <nav
      role="navigation"
      aria-label="Menú de navegación principal"
      className="relative z-base flex h-[69px] w-full flex-row border-b-[3px] border-b-primary bg-concrete py-0"
    >
      {/* Botón scroll izquierda */}
      {showScrollLeft && (
        <button
          onClick={scrollLeft}
          className="absolute left-0 top-0 z-10 flex h-full items-center bg-gradient-to-r from-concrete via-concrete to-transparent px-2 hover:from-gray-200"
          aria-label="Ver opciones anteriores"
        >
          <SvgCaret className="h-4 w-4 rotate-90 text-primary" />
        </button>
      )}

      <div
        ref={containerRef}
        onScroll={checkOverflow}
        role="menubar"
        aria-label="Menú de navegación principal"
        className="lg:inner mx-0 flex w-full flex-row items-center justify-between overflow-x-auto scrollbar-none"
      >
        <LinkNavigation
          title={'Inicio'}
          icon={SvgHouse}
          selected={'/' === route}
          route={'/'}
          uniqueId={'index'}
          isSticky={isSticky}
          className="!flex-[0_0_auto]"
        />
        {sortedDataNavigationItem.map((link, key) => {
          return (
            <LinkNavigation
              title={link.name}
              key={key}
              selected={compareLinkUrlAndRoute(asPath, link)}
              items={link.children || []}
              itemLink={link.linkItem}
              signLanguageGif={link.signLanguageGif}
              uniqueId={`index-${key}`}
              isSticky={isSticky}
            />
          );
        })}
      </div>

      {/* Botón scroll derecha */}
      {showScrollRight && (
        <button
          onClick={scrollRight}
          className="absolute right-0 top-0 z-10 flex h-full items-center bg-gradient-to-l from-concrete via-concrete to-transparent px-3 hover:from-gray-200"
          aria-label="Ver más opciones"
        >
          <SvgCaret className="h-4 w-4 -rotate-90 text-primary" />
        </button>
      )}
    </nav>
  );
};

export default NavigationBar;
